import { useEffect, useState } from "react";
import { Bitcoin, Loader2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorText, fmtTime, short, downloadFile } from "@/lib/api";

export const AnchorsPanel = ({ tick, pending, onChange }) => {
  const [anchors, setAnchors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(null);

  useEffect(() => {
    api.get("/anchors").then((r) => setAnchors(r.data)).catch(() => {});
  }, [tick]);

  const anchorNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/anchors");
      toast.success(`Anchor #${data.seq} submitted to Bitcoin calendars`);
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const check = async (seq) => {
    setChecking(seq);
    try {
      const { data } = await api.post(`/anchors/${seq}/check`);
      data.status === "confirmed" ? toast.success(`Confirmed in Bitcoin block ${data.bitcoin_block}`) : toast.info("Not in a block yet — usually takes a few hours");
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setChecking(null);
    }
  };

  return (
    <section className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-4 rounded-md border border-[#F7931A]/25 bg-[#141A24] p-6 space-y-5 h-fit" data-testid="bitcoin-anchor-batch-status">
        <div className="flex items-center gap-2 text-[#F7931A]"><Bitcoin className="h-5 w-5" /><span className="font-display font-semibold">Anchor to Bitcoin</span></div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Stamps the latest chain hash with OpenTimestamps. Because each entry links to the one before, one stamp proves every entry up to that point existed at that time.
        </p>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Waiting to anchor</p>
          <p className="font-mono text-3xl font-bold text-slate-50 mt-1" data-testid="pending-anchor-count">{pending}</p>
        </div>
        <Button onClick={anchorNow} disabled={busy || !pending} className="w-full bg-[#F7931A] hover:bg-[#ffa940] text-[#07090E] font-semibold" data-testid="bitcoin-anchor-now-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anchor now"}
        </Button>
      </div>

      <div className="lg:col-span-8 space-y-3" data-testid="bitcoin-anchor-history-table">
        {anchors.length === 0 && <p className="text-slate-500 text-sm py-10 text-center border border-dashed border-[#222D3D] rounded-md" data-testid="anchors-empty">No anchors yet.</p>}
        {anchors.map((a) => <AnchorRow key={a.seq} a={a} checking={checking === a.seq} onCheck={() => check(a.seq)} />)}
      </div>
    </section>
  );
};

const AnchorRow = ({ a, checking, onCheck }) => (
  <div className="rounded-md border border-[#222D3D] bg-[#0F141C] p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`anchor-row-${a.seq}`}>
    <div className="space-y-1 min-w-0">
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm text-slate-200">Anchor #{a.seq}</span>
        {a.status === "confirmed" ? (
          <span className="font-mono text-[11px] px-2 py-0.5 rounded border border-[#F7931A]/40 text-[#F7931A] bg-[#F7931A]/10" data-testid={`anchor-status-${a.seq}`}>block {a.bitcoin_block}</span>
        ) : (
          <span className="font-mono text-[11px] px-2 py-0.5 rounded border border-amber-500/30 text-amber-300" data-testid={`anchor-status-${a.seq}`}>pending confirmation</span>
        )}
      </div>
      <p className="text-xs text-slate-500">{fmtTime(a.created_at)} · entries up to #{a.head_entry_seq} ({a.entry_count} new)</p>
      <code className="font-mono text-xs text-emerald-400">{short(a.head_hash, 16)}</code>
    </div>
    <div className="flex gap-2 shrink-0">
      {a.status !== "confirmed" && (
        <Button size="sm" variant="outline" onClick={onCheck} disabled={checking} className="border-[#222D3D] bg-transparent" data-testid={`anchor-check-btn-${a.seq}`}>
          {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Check
        </Button>
      )}
      <Button size="sm" variant="outline" className="border-[#222D3D] bg-transparent" data-testid={`anchor-download-btn-${a.seq}`}
        onClick={async () => {
          await downloadFile(`/anchors/${a.seq}/file`, `doorman-anchor-${a.seq}.txt`);
          await downloadFile(`/anchors/${a.seq}/proof`, `doorman-anchor-${a.seq}.txt.ots`);
        }}>
        <Download className="h-3.5 w-3.5 mr-1.5" />Proof
      </Button>
    </div>
  </div>
);
